import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Skeleton,
  Alert,
  TextField,
  MenuItem,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SellIcon from "@mui/icons-material/Sell";
import InventoryIcon from "@mui/icons-material/Inventory2";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { dashboardApi, DashboardData, AvailableMonth } from "../../services/api";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function formatMonthLabel(m: AvailableMonth): string {
  return `${MONTH_NAMES[m.month - 1]} ${m.year}`;
}

function monthKey(m: AvailableMonth): string {
  return `${m.year}-${m.month}`;
}

function currency(value: string | number): string {
  return `$${Number(value).toFixed(2)}`;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}

function StatCard({ title, value, icon, color }: StatCardProps) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: { xs: 1.5, md: 2 }, "&:last-child": { pb: { xs: 1.5, md: 2 } } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {title}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: 1.5,
              p: 0.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              flexShrink: 0,
              ml: 1,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>("");

  const fetchDashboard = useCallback((month?: number, year?: number) => {
    setLoading(true);
    setError(null);
    dashboardApi
      .get(month && year ? { month, year } : undefined)
      .then((res) => {
        setData(res.data);
        setSelectedMonth(monthKey({ year: res.data.year, month: res.data.month }));
      })
      .catch(() => setError("Failed to load dashboard data."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleMonthChange = (value: string) => {
    setSelectedMonth(value);
    const [y, m] = value.split("-").map(Number);
    fetchDashboard(m, y);
  };

  if (loading && !data) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Dashboard</Typography>
        <Grid container spacing={{ xs: 1.5, md: 3 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid size={{ xs: 6, sm: 6, md: 3 }} key={i}>
              <Skeleton variant="rounded" height={80} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error && !data) {
    return <Alert severity="error">{error || "Unknown error"}</Alert>;
  }

  if (!data) return null;

  const netProfitNum = Number(data.net_profit);

  return (
    <Box>
      {/* Header with month selector */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Dashboard
        </Typography>
        <TextField
          select
          size="small"
          value={selectedMonth}
          onChange={(e) => handleMonthChange(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {data.available_months.map((m) => (
            <MenuItem key={monthKey(m)} value={monthKey(m)}>
              {formatMonthLabel(m)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Inventory Counts */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        Activity — {MONTH_NAMES[(data.month || 1) - 1]} {data.year}
      </Typography>
      <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: 3 }}>
        <Grid size={{ xs: 3, sm: 3, md: 3 }}>
          <StatCard
            title="Purchased"
            value={data.purchased_count}
            icon={<ShoppingCartIcon />}
            color="#2196F3"
          />
        </Grid>
        <Grid size={{ xs: 3, sm: 3, md: 3 }}>
          <StatCard
            title="Priced"
            value={data.priced_count}
            icon={<LocalOfferIcon />}
            color="#9C27B0"
          />
        </Grid>
        <Grid size={{ xs: 3, sm: 3, md: 3 }}>
          <StatCard
            title="In Booth"
            value={data.in_booth_count}
            icon={<StorefrontIcon />}
            color="#FF9800"
          />
        </Grid>
        <Grid size={{ xs: 3, sm: 3, md: 3 }}>
          <StatCard
            title="Sold"
            value={data.sold_count}
            icon={<SellIcon />}
            color="#4CAF50"
          />
        </Grid>
      </Grid>

      {/* Financial Summary */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        Financials — {MONTH_NAMES[(data.month || 1) - 1]} {data.year}
      </Typography>
      <Grid container spacing={{ xs: 1.5, md: 3 }}>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Total Invested"
            value={currency(data.total_invested)}
            icon={<TrendingDownIcon />}
            color="#E91E63"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Total Revenue"
            value={currency(data.total_revenue)}
            icon={<TrendingUpIcon />}
            color="#4CAF50"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Gross Profit"
            value={currency(data.gross_profit)}
            icon={<AccountBalanceIcon />}
            color="#9C27B0"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Net Profit"
            value={currency(data.net_profit)}
            icon={<ReceiptIcon />}
            color={netProfitNum >= 0 ? "#4CAF50" : "#f44336"}
          />
        </Grid>
      </Grid>

      {/* Fee Breakdown */}
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Fee Breakdown
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                CC Fees
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {currency(data.credit_card_fees)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Booth Commission
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {currency(data.booth_commission)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Booth Fee
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {currency(data.booth_fee)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Rent / Overhead
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {currency(data.rent_overhead)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
